import { Alert, Button, ButtonText, FormField, Spacer, TextInput, Typography } from '@percent/lemonade'
import { CentralCardLayoutWrapper } from '@percent/cause-dashboard/common/components'
import {
  useAuthState,
  useMutation,
  useAuthDispatch,
  useCausesPortalAnalytics,
  useGrantsAttributionRedirect
} from '@percent/cause-dashboard/common/hooks'
import { SET_AUTHORISED } from '@percent/cause-dashboard/context/auth'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { LoginType } from '@percent/cause-dashboard/services/auth/auth.types'
import { emailRegex } from '@percent/utility'
import { useLoginCaptcha } from '@percent/cause-dashboard/common/hooks/useAuthCaptcha/useAuthCaptcha'
import { FormikProvider, useFormik } from 'formik'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import * as yup from 'yup'
import { RoutePath } from '../../routes/Routes'
import styles from './LoginPage.module.scss'

const REQUIRED_2FA_ERROR = 'auth/requires_two_fa_setup'
const REQUIRED_2FA_LOGIN = 'auth/requires_two_fa_login'

export function LoginPage() {
  const getCaptchaHeaders = useLoginCaptcha()
  const { authService } = useServices()
  const { authDispatch } = useAuthDispatch()
  const { authState } = useAuthState()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { track, identify } = useCausesPortalAnalytics()

  const { search, state } = useLocation()
  const urlParams = new URLSearchParams(search)
  const emailVerified = (state as { emailVerified?: boolean } | null)?.emailVerified === true

  const nextPath = urlParams.get('next')
  const redirectAfterLogin = nextPath && nextPath !== RoutePath.SIGNIN && nextPath.length > 0

  const utmParams = {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content')
  }

  useGrantsAttributionRedirect()

  useEffect(() => {
    // check if that is required, authReducer should handle authorisation, so it can be redundant, but will have to check that after
    // releasing the API
    if (authState.isAuthorised) {
      navigate('/nonprofits')
    }
  }, [authState.isAuthorised, navigate])

  const loginAndUpdateUser = async (loginDetails: LoginType) =>
    authService
      .login(
        {
          ...loginDetails,
          enforce: true
        },
        await getCaptchaHeaders()
      )
      .then(async resp => {
        const { data } = resp.data
        const { id } = data

        await identify({ id, traits: { email: loginDetails?.email } })
        await track('Logged In', {
          accountId: authState.user?.id,
          ...utmParams
        })

        return authDispatch({
          type: SET_AUTHORISED,
          payload: {}
        })
      })
      .catch(error => {
        if (error.response.status === 400 && error.response.data.error.code === REQUIRED_2FA_ERROR) {
          navigate('/signin/2fa-setup', {
            state: { password: loginDetails.password }
          })
        } else if (error.response.status === 400 && error.response.data.error.code === REQUIRED_2FA_LOGIN) {
          navigate('/signin/2fa', {
            state: { password: loginDetails.password }
          })
        } else {
          throw error
        }
      })

  const [{ errorMessage, isLoading }, { apiFunc: loginUser }] = useMutation(loginAndUpdateUser)

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema: () =>
      yup.object().shape({
        email: yup.string().required('Required').trim().matches(emailRegex, t('errorMessage.validEmail')),
        password: yup.string().required('Required')
      }),
    onSubmit: (loginDetails: LoginType) => {
      loginUser(loginDetails)
    }
  })

  const { errors, values, touched, handleChange, handleBlur, handleSubmit } = formik

  return (
    <CentralCardLayoutWrapper
      heading={t('form.signIn')}
      handleSubmit={handleSubmit}
      backgroundImages
      error={errorMessage}
    >
      {emailVerified && (
        <>
          <Alert variant="success" title={t('accountVerification.emailVerified')}>
            {t('accountVerification.signInToContinue')}
          </Alert>
          <Spacer size={4} axis="vertical" />
        </>
      )}
      {redirectAfterLogin && !emailVerified && (
        <>
          <Alert
            variant="info"
            title={
              nextPath.includes(RoutePath.AI_GATEWAY)
                ? t('form.signInToContinueToGoodstackAI')
                : t('form.signInToContinue')
            }
          />
          <Spacer size={4} axis="vertical" />
        </>
      )}
      <FormikProvider value={formik}>
        <FormField
          label={t('form.email')}
          status={touched.email && errors.email ? 'danger' : 'default'}
          statusMessage={errors.email}
          data-testid="email"
          necessity="required"
        >
          <TextInput
            name="email"
            placeholder={t('form.enterEmailAddress')}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
          />
        </FormField>
        <Spacer size={4} axis="vertical" />
        <FormField
          label={t('form.password')}
          status={touched.password && errors.password ? 'danger' : 'default'}
          statusMessage={errors.password}
          data-testid="password"
          necessity="required"
        >
          <TextInput
            type="password"
            name="password"
            placeholder={t('form.passwordPlaceholder')}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.password}
          />
        </FormField>
        <Spacer size={4} axis="vertical" />
        <div className={styles.forgotPasswordText}>
          <ButtonText onClick={() => navigate(RoutePath.FORGOT_PASSWORD)}>{t('form.forgotPassword')}</ButtonText>
        </div>
        <Button size="large" stretch data-testid="auth-active-button" type="submit" loading={isLoading}>
          {t('form.signIn')}
        </Button>
        <div className={styles.loginFormFooter}>
          <Typography variant="default" className={styles.noAccountYet}>
            {t('form.noAccount')}
          </Typography>
          <ButtonText onClick={() => navigate(RoutePath.CLAIM)}>{t('form.nonProfitAndSignUp')}</ButtonText>
        </div>
      </FormikProvider>
    </CentralCardLayoutWrapper>
  )
}
